import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { MDRBadge } from "@/components/MDRBadge";
import { toast } from "sonner";
import {
  accreditedStructuresData, specialistAssignmentsData, getStructureById
} from "@/data/platformData";
import { doctorsData, servicesData } from "@/data/mockData";
import { Stethoscope, ArrowLeft, Save, Mail, Phone, BadgeCheck } from "lucide-react";

const STRUCTURE_ID = "str1";

export const StructureSpecialistsPage = () => {
  const navigate = useNavigate();
  const structure = getStructureById(STRUCTURE_ID);
  const allowedServices = servicesData.filter(s => structure.handledServices.includes(s.id));

  // Build assignment matrix: for each specialist, set of services
  const initialMatrix = {};
  doctorsData.forEach(d => {
    const found = specialistAssignmentsData.find(a => a.specialistId === d.id && a.structureId === STRUCTURE_ID);
    initialMatrix[d.id] = {
      enabled: structure.specialistIds.includes(d.id),
      services: new Set(found?.services || [])
    };
  });

  const [matrix, setMatrix] = useState(initialMatrix);

  const toggleSpecialist = (id) => {
    setMatrix(prev => ({ ...prev, [id]: { ...prev[id], enabled: !prev[id].enabled } }));
  };
  const toggleService = (specId, srvId) => {
    setMatrix(prev => {
      const next = new Set(prev[specId].services);
      if (next.has(srvId)) next.delete(srvId); else next.add(srvId);
      return { ...prev, [specId]: { ...prev[specId], services: next } };
    });
  };

  const save = () => {
    const total = Object.values(matrix).filter(m => m.enabled).reduce((acc, m) => acc + m.services.size, 0);
    toast.success("Assegnazioni salvate", { description: `${total} associazioni prestazione↔specialista attive.` });
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
      <div>
        <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate("/structure")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Panoramica
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Stethoscope className="h-4 w-4 text-cyan-600" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Medici Specialisti</span>
              <MDRBadge variant="admin" size="sm" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Assegnazione prestazioni</h1>
            <p className="text-sm text-muted-foreground mt-1">{structure.name}</p>
          </div>
          <Button variant="premium" onClick={save} className="gap-2" data-testid="specialists-save-btn">
            <Save className="h-4 w-4" /> Salva matrice
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {doctorsData.map(d => {
          const row = matrix[d.id];
          return (
            <Card key={d.id} className={!row.enabled ? "opacity-60" : ""} data-testid={`specialist-card-${d.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">{d.name}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">{d.specialization} · {d.licenseNumber}</p>
                  </div>
                  <Switch checked={row.enabled} onCheckedChange={() => toggleSpecialist(d.id)} data-testid={`specialist-toggle-${d.id}`} />
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-2">
                  <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {d.email}</span>
                  <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {d.phone}</span>
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1 text-[10px]">
                    <BadgeCheck className="h-3 w-3" /> Firma {d.digitalSignatureId}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 border-t pt-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Prestazioni abilitate</p>
                <div className="grid grid-cols-2 gap-2">
                  {allowedServices.map(s => {
                    const checked = row.services.has(s.id);
                    return (
                      <label key={s.id} className={`flex items-center gap-2 text-sm cursor-pointer p-2 rounded-md border ${checked ? "bg-cyan-50 border-cyan-200" : "bg-card"} ${!row.enabled ? "pointer-events-none" : ""}`}>
                        <Checkbox checked={checked} onCheckedChange={() => toggleService(d.id, s.id)} data-testid={`specialist-service-${d.id}-${s.id}`} />
                        <span className="truncate">{s.name}</span>
                      </label>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default StructureSpecialistsPage;
